import { Component, EventEmitter, inject, Input, OnChanges, Output, SecurityContext, SimpleChanges } from '@angular/core';

import { DomSanitizer } from '@angular/platform-browser';
import { EImageSize, ProductFlatDto, ProductIdentifierDto, ProductImageDto } from 'projects/shared/lib/services/INTEGRA_Shop_Client';
import { LoggerService } from 'projects/shared/lib/services/infrastructure/logger/logger.service';
import { SearchPreviewItem } from './search-preview-item.model';
import { ISearchEnvironment } from 'projects/shared/lib/interfaces/environment/search/search-environment.interface';
import { ENVIRONMENT } from 'projects/shared/lib/environment/environment';
import { IProductService, IProductServiceToken } from 'projects/shared/lib/services/data/product/product-service.interface';
import { firstValueFrom } from 'rxjs';
import { ServiceLocator } from 'projects/shared/lib/services/infrastructure/service-locator.service';
import { IComparisonService, IComparisonServiceToken } from 'projects/shared/lib/services/infrastructure/comparison/comparison-service.interface';

@Component({
  selector: 'app-search-preview-item',
  imports: [],
  templateUrl: './search-preview-item.component.html',
  styleUrls: ['./search-preview-item.component.scss']
})
export class SearchPreviewItemComponent implements OnChanges {
  @Input() public productFlat: ProductFlatDto | undefined;
  @Input() public searchPreviewItem: SearchPreviewItem | undefined;
  @Output() public onProductSearchResultClicked: EventEmitter<void> = new EventEmitter<void>();

  private searchSetting: ISearchEnvironment | undefined;

  private readonly logger: LoggerService;
  private readonly productService: IProductService;
  private readonly sanitizer: DomSanitizer;
  private readonly comparisonService: IComparisonService;

  public constructor() {
    this.logger = ServiceLocator.injector.get(LoggerService);
    this.productService = inject(IProductServiceToken);
    this.sanitizer = ServiceLocator.injector.get(DomSanitizer);
    this.comparisonService = inject(IComparisonServiceToken);
    this.searchSetting = ENVIRONMENT.search;
  }

  public ngOnChanges(changes: SimpleChanges): void {
    for (let propName in changes) {
      let change = changes[propName];
      switch (propName) {
        case 'productFlat': {
          if (this.searchPreviewItem == undefined) {
            this.initByProductFlatDto(change.currentValue);
          }
          break;
        }
        case 'searchPreviewItem': {
          this.initBySearchPreviewItem(change.currentValue);
          break;
        }
        default: {
          throw new Error('Input property handler not implemented.');
        }
      }
    }
  }

  private async initByProductFlatDto(change: any) {
    let productFlatDto: ProductFlatDto = change as ProductFlatDto;
    this.productFlat = productFlatDto;
    this.searchPreviewItem = new SearchPreviewItem(productFlatDto.productIdentifier);
    await this.productThumbnailLoadAsync(this.searchPreviewItem);
  }

  private initBySearchPreviewItem(change: any) {
    let productSearchPreviewItem: SearchPreviewItem = change as SearchPreviewItem;
    this.searchPreviewItem = productSearchPreviewItem;
  }

  private async productThumbnailLoadAsync(productSearchPreviewItem: SearchPreviewItem) {
    let productIdentifier: ProductIdentifierDto | undefined = productSearchPreviewItem.productIdentifier;
    if (!productIdentifier) {
      throw new Error('Missing productIdentifier upon loading the thumbnail in the search preview.');
    }

    let thumbnailSize = this.searchSetting?.previewImageSize;
    if (thumbnailSize == undefined) {
      thumbnailSize = EImageSize.Small;
    }

    try {
      let productThumbnailGetQueryResult = await firstValueFrom(this.productService.productThumbnailGet(thumbnailSize, [productIdentifier]));

      let productImageDtoList: ProductImageDto[] | undefined = productThumbnailGetQueryResult.productList;
      if (!productImageDtoList || productImageDtoList.length < 1) {
        return;
      }

      let thumbnailDto: ProductImageDto | undefined = productImageDtoList.find(
        (productImage: ProductImageDto) => this.comparisonService.equalsProductIdentifierByNumberOrGuid(productImage.productIdentifier, productIdentifier)
      );

      if (!thumbnailDto) {
        this.logger.info(`Thumbnail for product#: '${productIdentifier.productNumber}' could not be loaded.`, this.constructor.name);
        return;
      }

      let sanitizedThumbnail = this.sanitizer.sanitize(SecurityContext.URL, `data:${thumbnailDto.contentType};base64,${thumbnailDto.content}`);
      if (!sanitizedThumbnail) {
        return;
      }

      productSearchPreviewItem.thumbnail = sanitizedThumbnail;
    } catch (error) {
      this.logger.warn(error, this.constructor.name);
    }
  }
}
